import {
  lazy,
  Suspense,
  type ComponentType,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { ProtectedRoute } from './protected';
import type {
  IngredientDetail,
  LogEntry,
  MealItem,
  MealTime,
  Recipe,
  ScheduleByDate,
  ShoppingItem,
  UserInventory,
  UserMealTimeConfig,
  UserProfile,
} from '../types';

function lazyNamed<TProps>(
  loader: () => Promise<Record<string, unknown>>,
  exportName: string
) {
  return lazy(async () => {
    const module = await loader();
    return {
      default: module[exportName] as ComponentType<TProps>,
    };
  });
}

const ScheduleView = lazyNamed<any>(() => import('../views/ScheduleView'), 'ScheduleView');
const RecipesViewWrapper = lazyNamed<any>(() => import('../views/RecipesViewWrapper'), 'RecipesViewWrapper');
const RecipeDetailViewWrapper = lazyNamed<any>(() => import('../views/RecipeDetailViewWrapper'), 'RecipeDetailViewWrapper');
const IngredientsViewWrapper = lazyNamed<any>(() => import('../views/IngredientsViewWrapper'), 'IngredientsViewWrapper');
const ShoppingListViewWrapper = lazyNamed<any>(() => import('../views/ShoppingListViewWrapper'), 'ShoppingListViewWrapper');
const UserProfileViewWrapper = lazyNamed<any>(() => import('../views/UserProfileViewWrapper'), 'UserProfileViewWrapper');
const DevAdminViewWrapper = lazyNamed<any>(() => import('../views/DevAdminViewWrapper'), 'DevAdminViewWrapper');
const ComponentLibraryViewWrapper = lazyNamed<any>(() => import('../views/ComponentLibraryViewWrapper'), 'ComponentLibraryViewWrapper');
const LogHistoryViewWrapper = lazyNamed<any>(() => import('../views/LogHistoryViewWrapper'), 'LogHistoryViewWrapper');

/**
 * RecipesViewWrapper with query params support
 * 读取URL查询参数并传递给组件
 */
function RecipesViewWrapperWithQuery(props: any) {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || undefined;

  return <RecipesViewWrapper {...props} mode={mode} />;
}

// 路由类型定义
export interface RouteConfig {
  path: string;
  component: React.ComponentType<any>;
  protected?: boolean;
}

/**
 * 应用路由配置
 * 集中管理所有路由定义
 */
export const appRoutes: RouteConfig[] = [
  {
    path: '/',
    component: ScheduleView,
  },
  {
    path: '/recipes',
    component: RecipesViewWrapper,
  },
  {
    path: '/recipe/:recipeId',
    component: RecipeDetailViewWrapper,
  },
  {
    path: '/ingredients',
    component: IngredientsViewWrapper,
  },
  {
    path: '/shopping',
    component: ShoppingListViewWrapper,
  },
  {
    path: '/profile',
    component: UserProfileViewWrapper,
  },
  {
    path: '/devadmin',
    component: DevAdminViewWrapper,
    protected: true,
  },
  {
    path: '/devadmin/component-library',
    component: ComponentLibraryViewWrapper,
    protected: true,
  },
  {
    path: '/log-history',
    component: LogHistoryViewWrapper,
  },
];

/**
 * AppRouter Props
 */
interface AppRouterProps {
  selectedDate: string;
  setSelectedDate: (date: string) => void;
  currentSchedule: MealTime[];
  scheduleByDate: ScheduleByDate;
  recipes: Recipe[];
  ingredients: IngredientDetail[];
  shoppingList: ShoppingItem[];
  user: UserProfile;
  inventory: UserInventory;
  logs: LogEntry[];
  setLogs: Dispatch<SetStateAction<LogEntry[]>>;
  customMealTimes: UserMealTimeConfig[];
  isAuthenticated: boolean;
  onThemeToggle: () => void;
  onLogout: () => Promise<void>;
  // 用户目标更新方法
  updateNutritionGoals?: (goals: any, showToastMessage: (msg: string) => void) => Promise<void>;
  updateBodyData?: (data: any, showToastMessage: (msg: string) => void) => Promise<void>;
  updateDietaryPreferences?: (preferences: any, showToastMessage: (msg: string) => void) => Promise<void>;
  updateBasicInfo?: (info: any, showToastMessage: (msg: string) => void) => Promise<void>;
  onFindRecipe: (ingredientName?: string) => void;
  onViewLog: () => void;
  onOpenLogCenter: () => void;
  onCopySchedule: (sourceDate: string, targetDate: string) => void;
  onDeleteSchedule: (date: string) => void;
  onGenerateShoppingListFromSchedule: (date: string) => void;
  onMealItemClick: (mealItem: MealItem) => void;
  onMealComplete: (mealTitle: string, itemId: string, completed: boolean) => void;
  onAddDish: (mealTitle: string) => void;
  onDeleteMealItem: (mealTitle: string, itemId: string) => void;
  onUpdateMealItem: (mealTitle: string, itemId: string, updates: Partial<MealItem>) => void;
  onViewRecipe: (recipeId: string) => void;
  onApplyCalorieSuggestion: (recipeId: string, calories: number) => void;
  onRateRecipe: (recipeId: string, type: 'difficulty' | 'love', value: number) => Promise<void>;
  onClearRecipeRating: (recipeId: string, type: 'difficulty' | 'love') => Promise<void>;
  onAIGenerate: (recipe: Recipe, mealTitle: string) => Promise<void>;
  getScheduleStats: (start: string, end: string) => any;
  onSaveMealTimeConfigs: (configs: UserMealTimeConfig[]) => Promise<void>;
  onAddMealTimeConfig: (config: Omit<UserMealTimeConfig, 'id'>) => Promise<void>;
  onUpdateMealTimeConfig: (id: string, config: Partial<UserMealTimeConfig>) => Promise<void>;
  onDeleteMealTimeConfig: (id: string) => Promise<void>;
  onGenerateShoppingListFromPlan: () => void;
  recipeId?: string;
  onCloseRecipeDetail: () => void;
  onAddToShoppingList: (
    ingredientName: string,
    options?: {
      linkedIngredientId?: string;
      quantity?: string;
      date?: string;
    }
  ) => void;
  onToggleIngredientFavorite: (ingredientId: string) => void;
  onAddToPlan: (recipe: Recipe) => void;
  onToggleFavorite: (recipeId: string) => void;
  onEditRecipe: (recipe: Recipe) => void;
  onSaveBatch: (ingredientId: string, batch: any) => void;
  onCheckout: (items: ShoppingItem[]) => void;
  onNavigateToIngredient: (ingredientId?: string) => void;
  onNewRecipe: () => void;
  setRecipes: Dispatch<SetStateAction<Recipe[]>>;
  setIngredients: Dispatch<SetStateAction<IngredientDetail[]>>;
  setShoppingList: Dispatch<SetStateAction<ShoppingItem[]>>;
  showToastMessage: (message: string) => void;
  awardXP: (amount: number) => void;
  onViewChange?: (view: string) => void;
}

/**
 * AppRouter 组件
 * 应用路由主容器
 */
export function AppRouter(props: AppRouterProps) {
  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<ScheduleView {...props} />} />
        <Route
          path="/recipes"
          element={
            <RecipesViewWrapperWithQuery
              recipes={props.recipes}
              ingredients={props.ingredients}
              inventory={props.inventory}
              user={props.user}
              onSelectRecipe={(recipe: Recipe) => props.onViewRecipe(recipe.id)}
              onToggleFavorite={props.onToggleFavorite}
              onAddToPlan={props.onAddToPlan}
              onNewRecipe={props.onNewRecipe}
              onRecipeCreated={async () => Promise.resolve()}
              setRecipes={props.setRecipes}
              showToastMessage={props.showToastMessage}
              awardXP={props.awardXP}
            />
          }
        />
        <Route
          path="/recipe/:recipeId"
          element={
            <RecipeDetailViewWrapper
              recipes={props.recipes}
              ingredients={props.ingredients}
              inventory={props.inventory}
              user={props.user}
              onAddToShoppingList={props.onAddToShoppingList}
              onAddToPlan={props.onAddToPlan}
              onToggleFavorite={props.onToggleFavorite}
              onEditRecipe={props.onEditRecipe}
              onRateRecipe={props.onRateRecipe}
              onClearRecipeRating={props.onClearRecipeRating}
              setRecipes={props.setRecipes}
              showToastMessage={props.showToastMessage}
              awardXP={props.awardXP}
            />
          }
        />
        <Route
          path="/ingredients"
          element={
            <IngredientsViewWrapper
              inventory={props.inventory}
              ingredients={props.ingredients}
              shoppingList={props.shoppingList}
              user={props.user}
              onFindRecipe={props.onFindRecipe}
              onSaveBatch={props.onSaveBatch}
              onAddToShoppingList={props.onAddToShoppingList}
              onToggleIngredientFavorite={props.onToggleIngredientFavorite}
              onAIGenerate={(recipe: Recipe) => props.onAIGenerate(recipe, '智能加餐')}
            />
          }
        />
        <Route
          path="/shopping"
          element={
            <ShoppingListViewWrapper
              shoppingList={props.shoppingList}
              setShoppingList={props.setShoppingList}
              onCheckout={props.onCheckout}
              onNavigateToIngredient={props.onNavigateToIngredient}
              showToastMessage={props.showToastMessage}
              scheduleByDate={props.scheduleByDate}
              selectedDate={props.selectedDate}
              setSelectedDate={props.setSelectedDate}
              recipes={props.recipes}
              ingredients={props.ingredients}
              inventory={props.inventory}
            />
          }
        />
        <Route
          path="/profile"
          element={
            <UserProfileViewWrapper
              user={props.user}
              recipes={props.recipes}
              ingredients={props.ingredients}
              isAuthenticated={props.isAuthenticated}
              onThemeToggle={props.onThemeToggle}
              onLogout={props.onLogout}
              showToastMessage={props.showToastMessage}
              updateNutritionGoals={props.updateNutritionGoals}
              updateBodyData={props.updateBodyData}
              updateDietaryPreferences={props.updateDietaryPreferences}
            />
          }
        />
        <Route
          path="/devadmin"
          element={
            <ProtectedRoute requireAdmin>
              <DevAdminViewWrapper
                recipes={props.recipes}
                ingredients={props.ingredients}
                setRecipes={props.setRecipes}
                setIngredients={props.setIngredients}
                showToastMessage={props.showToastMessage}
                awardXP={props.awardXP}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/devadmin/component-library"
          element={
            <ProtectedRoute requireAdmin>
              <ComponentLibraryViewWrapper />
            </ProtectedRoute>
          }
        />
        <Route
          path="/log-history"
          element={
            <LogHistoryViewWrapper
              userId={props.user.id}
              logs={props.logs}
              inventory={props.inventory}
              ingredients={props.ingredients}
              onFindRecipe={props.onFindRecipe}
              onClose={props.onCloseRecipeDetail}
              showToastMessage={props.showToastMessage}
              setLogs={props.setLogs}
            />
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
